/**
 * LoginScreen.jsx
 * User authentication screen with StrongFit branding
 * Includes forgot password functionality
 */
import React, { useState } from 'react';
import { CheckCircle, RefreshCw, ArrowLeft, Mail, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import WinterHat from '../WinterHat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const LoginScreen = ({ 
  onLogin, 
  loginError, 
  setLoginError, 
  winterHatEnabled = false,
  logoUrl = '/assets/invictafit-logo.png',
  logoSize = 100,
  logoType = 'image'
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      setLoginError(t('login.enter_email_password'));
      return;
    }
    setIsLoading(true);
    await onLogin(email, password);
    setIsLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error(t('login.enter_email'));
      return;
    }

    setSendingReset(true);
    try {
      await axios.post(`${API}/portal/auth/forgot-password`, {
        email: forgotEmail
      });
      setResetSent(true);
      toast.success(t('login.email_was_sent'));
    } catch (error) {
      // Always show success to prevent email enumeration
      setResetSent(true);
    } finally {
      setSendingReset(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setResetSent(false);
  };

  // StrongFit gold color
  const goldColor = '#C9A227';

  // Forgot Password Screen
  if (showForgotPassword) {
    return (
      <div className="h-full flex flex-col p-6 relative z-10">
        {/* Header */}
        <div className="pt-2 pb-4">
          <button 
            onClick={resetForgotPassword}
            className="flex items-center gap-2 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">{t('common.back')}</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center">
          {resetSent ? (
            // Success State
            <div className="text-center">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${goldColor}20` }}
              >
                <Check className="w-8 h-8" style={{ color: goldColor }} />
              </div>
              <h1 className="text-white text-xl font-bold mb-2">{t('login.email_sent_title')}</h1>
              <p className="text-zinc-400 text-sm mb-6 max-w-xs">
                {t('login.email_sent_desc')}
              </p>
              <p className="text-zinc-500 text-xs mb-6">
                {t('login.check_spam')}
              </p>
              <button 
                onClick={resetForgotPassword}
                className="w-full text-black font-semibold py-3 rounded-xl"
                style={{ backgroundColor: goldColor }}
              >
                {t('login.back_to_login')}
              </button>
            </div>
          ) : (
            // Form State
            <>
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: `${goldColor}20` }}
              >
                <Mail className="w-8 h-8" style={{ color: goldColor }} />
              </div>
              <h1 className="text-white text-xl font-bold mb-2">{t('login.forgot_title')}</h1>
              <p className="text-zinc-400 text-sm mb-6 text-center max-w-xs">
                {t('login.forgot_desc')}
              </p>

              <div className="w-full space-y-4">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">{t('login.email_address')}</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder={t('login.email_placeholder')}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-500 focus:outline-none"
                    onFocus={(e) => e.target.style.borderColor = goldColor}
                    onBlur={(e) => e.target.style.borderColor = '#3f3f46'}
                    onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
                  />
                </div>

                <button 
                  onClick={handleForgotPassword}
                  disabled={sendingReset || !forgotEmail}
                  className="w-full text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: goldColor }}
                >
                  {sendingReset ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    t('login.send_link')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Main Login Screen
  return (
    <div className="h-full flex flex-col p-6 relative z-10 bg-transparent">
      {/* Header */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {/* StrongFit Logo with Glow */}
        <div 
          className="relative rounded-2xl flex items-center justify-center mb-4"
          style={{
            width: `${80 * (logoSize / 100)}px`,
            height: `${80 * (logoSize / 100)}px`,
            backgroundColor: '#000000',
            boxShadow: `0 0 25px rgba(201, 162, 39, 0.5), 0 0 50px rgba(201, 162, 39, 0.25)`,
            border: '2px solid rgba(201, 162, 39, 0.4)'
          }}
        >
          {/* Winter Hat */}
          {winterHatEnabled && <WinterHat size="medium" enabled={winterHatEnabled} />}
          
          {/* Inner glow */}
          <div 
            className="absolute inset-0 rounded-2xl"
            style={{
              background: 'radial-gradient(circle at center, rgba(201, 162, 39, 0.15) 0%, transparent 70%)'
            }}
          />
          {logoType === 'video' ? (
            <video 
              src={logoUrl}
              autoPlay
              loop
              muted
              playsInline
              className="object-contain relative z-10"
              style={{
                width: `${56 * (logoSize / 100)}px`,
                height: `${56 * (logoSize / 100)}px`,
                filter: 'drop-shadow(0 0 8px rgba(201, 162, 39, 0.5))'
              }}
            />
          ) : (
            <img 
              src={logoUrl}
              alt="Logo"
              className="object-contain relative z-10"
              style={{
                width: `${56 * (logoSize / 100)}px`,
                height: `${56 * (logoSize / 100)}px`,
                filter: 'drop-shadow(0 0 8px rgba(201, 162, 39, 0.5))'
              }}
            />
          )}
        </div>
        
        <h1 className="text-white text-xl font-bold mb-1">{t('login.welcome_back')}</h1>
        <p className="text-zinc-500 text-xs mb-6">{t('login.sign_in_continue')}</p>

        {/* Error Message */}
        {loginError && (
          <div className="w-full bg-red-500/20 border border-red-500/50 rounded-xl px-4 py-2 mb-3">
            <p className="text-red-400 text-xs text-center">{loginError}</p>
          </div>
        )}

        {/* Login Form */}
        <div className="w-full space-y-3">
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">{t('login.email_label')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setLoginError(''); }}
              placeholder={t('login.email_placeholder')}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-500 focus:outline-none"
              style={{ 
                '--tw-ring-color': goldColor,
              }}
              onFocus={(e) => e.target.style.borderColor = goldColor}
              onBlur={(e) => e.target.style.borderColor = '#3f3f46'}
            />
          </div>
          
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">{t('login.password_label')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
              placeholder="••••••••"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-500 focus:outline-none"
              onFocus={(e) => e.target.style.borderColor = goldColor}
              onBlur={(e) => e.target.style.borderColor = '#3f3f46'}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setRememberMe(!rememberMe)}
              className="flex items-center gap-2"
            >
              <div 
                className={`w-5 h-5 rounded border flex items-center justify-center`}
                style={{ 
                  backgroundColor: rememberMe ? goldColor : 'transparent',
                  borderColor: rememberMe ? goldColor : '#52525b'
                }}
              >
                {rememberMe && <CheckCircle className="w-3 h-3 text-black" />}
              </div>
              <span className="text-zinc-400 text-xs">{t('login.remember_me')}</span>
            </button>
            {/* Iter 318 (Feb 2026): "Passwort vergessen?" entfernt –
                OTP-only Flow für Kunden, identisch mit echter Mobile-App. */}
          </div>

          {/* Login Button */}
          <button 
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2 mt-4 transition-all"
            style={{ 
              backgroundColor: goldColor,
              boxShadow: '0 0 15px rgba(201, 162, 39, 0.3)'
            }}
          >
            {isLoading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <>{t('login.sign_in')}</>
            )}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 my-4 w-full">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-zinc-600 text-xs">{t('login.or')}</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        {/* Google Login */}
        <button className="w-full bg-zinc-900 border border-zinc-700 py-3 rounded-xl flex items-center justify-center gap-3 hover:border-zinc-500 transition-colors">
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"/>
            <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"/>
            <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"/>
            <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"/>
          </svg>
          <span className="text-white text-sm">{t('login.sign_in_google')}</span>
        </button>
      </div>

      {/* Footer */}
      <div className="text-center pt-4">
        <p className="text-zinc-600 text-xs">
          {t('login.no_account')} <button style={{ color: goldColor }}>{t('login.register')}</button>
        </p>
      </div>
    </div>
  );
};

export default LoginScreen;
